
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { SleepFormData } from "../SleepFormContext";
import { StepType } from "./sleep-dialog-types";
import { UseFormReturn } from "react-hook-form";
import { useSleepUpdateService } from "./services/sleep-update-service";
import { useSleepCreateService } from "./services/sleep-create-service";
import { logSubmissionData, validateSleepFormData } from "./utils/submission-utils";
import { HandleSubmitParams } from "./types/submission-types";

export function useSleepDialogSubmit() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { updateSleepSession } = useSleepUpdateService();
  const { createSleepSession } = useSleepCreateService();

  const handleSubmit = async (
    values: SleepFormData,
    isEditing: boolean,
    activityToEdit: any,
    setSaving: (saving: boolean) => void,
    onOpenChange: (open: boolean) => void,
    onSuccess?: () => void,
    formMethods?: UseFormReturn<SleepFormData>, 
    setCurrentStep?: (step: StepType) => void,
    setShowConfirmation?: (show: boolean) => void 
  ): Promise<boolean> => {
    try {
      setSaving(true);
      
      // Log form data for debugging
      logSubmissionData(values);

      // Validate form data
      if (!validateSleepFormData(values, toast)) {
        setSaving(false);
        return false;
      }

      let success = false;
      
      if (isEditing && activityToEdit?.id) {
        success = await updateSleepSession(values, activityToEdit);
      } else {
        success = await createSleepSession(values);
      }

      if (success) {
        // Reset form and UI state if successful
        if (formMethods) {
          console.log(' Resetting form after successful submission');
          formMethods.reset();
        }
        
        if (setCurrentStep) {
          console.log(' Resetting current step to start');
          setCurrentStep('start');
        }

        if (setShowConfirmation) {
          console.log(' Resetting confirmation dialog state');
          setShowConfirmation(false);
        }

        setSaving(false);
        onOpenChange(false);

        if (onSuccess) {
          onSuccess();
        }

        return true;
      } else {
        setSaving(false);
        return false;
      }

    } catch (error) {
      console.error(' Error in sleep session create/update:', error);
      
      setSaving(false);
      
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to submit sleep session"
      });

      return false;
    }
  };

  return { handleSubmit };
}
